import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Download, Pencil, Repeat, Ban, CheckCircle2, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { exportPdf, pdfBase64 } from "@/lib/format";
import { useApp } from "@/context/AppCtx";
import { PageHeader, StatusBadge, Confirm, PrintPortal } from "@/components/kit";
import { PrintDocument } from "@/components/PrintDocument";
import { PayDialog } from "@/components/PayDialog";
import { PaymentsPanel } from "@/components/PaymentsPanel";
import { EmailStatus } from "@/components/EmailStatus";

export default function DocView({ kind }) {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { settings, base } = useApp();
  const isQ = kind === "quote";
  const path = isQ ? "quotes" : "invoices";
  const [doc, setDoc] = useState(null);
  const [pay, setPay] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [mailing, setMailing] = useState(false);
  const ref = useRef(null);

  const load = useCallback(() => api.get(`/${path}/${id}`).then((r) => setDoc(r.data)).catch(() => setDoc(false)), [path, id]);
  useEffect(() => {
    load();
  }, [load]);

  const post = async (url, body, msg, goto) => {
    try {
      const { data } = await api.post(url, body);
      toast.success(msg);
      if (goto) {
        nav(`${base}/facturas/${data.id}`, { state: { autoSend: settings.auto_email !== false } });
      }
      else load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pdf = async () => {
    setPdfBusy(true);
    try {
      await exportPdf(ref.current, `${doc.number}.pdf`);
    } finally {
      setPdfBusy(false);
    }
  };

  const sendEmail = useCallback(async () => {
    setMailing(true);
    try {
      const pdf_base64 = await pdfBase64(ref.current);
      const { data } = await api.post(`/${path}/${id}/send-email`, { pdf_base64 });
      toast.success(`Enviado por correo a ${data.last_email.to} con PDF adjunto`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMailing(false);
      load();
    }
  }, [path, id, load]);

  // Envío automático al crear: espera a que el documento se dibuje para generar el PDF
  useEffect(() => {
    if (!doc || !location.state?.autoSend) return;
    nav(location.pathname, { replace: true, state: {} });
    if (doc.client?.email) setTimeout(sendEmail, 400);
    else toast.warning("No se envió por correo: el cliente no tiene correo registrado");
  }, [doc, location, nav, sendEmail]);

  const del = async (p) => {
    try {
      await api.delete(`/invoices/${doc.id}/payments/${p.id}`);
      toast.success("Abono eliminado");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (doc === false) return <div className="text-slate-400">Documento no encontrado.</div>;
  if (!doc) return <div className="text-slate-400">Cargando…</div>;

  return (
    <div>
      <PageHeader title={`${isQ ? "Proforma" : "Factura"} ${doc.number}`} subtitle={doc.client?.name}>
        <Button variant="ghost" onClick={() => nav(`${base}/${isQ ? "proformas" : "facturas"}`)} data-testid="view-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Volver</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="view-print-btn"><Printer className="w-4 h-4 mr-1" />Imprimir</Button>
        <Button variant="outline" onClick={pdf} disabled={pdfBusy} data-testid="view-pdf-btn">
          {pdfBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}PDF
        </Button>
        {doc.status !== "anulada" && (
          <Button variant="outline" onClick={sendEmail} disabled={mailing} data-testid="view-email-btn">
            {mailing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}{mailing ? "Enviando…" : "Enviar por correo"}
          </Button>
        )}
        {isQ && doc.status === "vigente" && (
          <>
            <Button variant="outline" onClick={() => nav(`${base}/proformas/${doc.id}/editar`)} data-testid="view-edit-btn"><Pencil className="w-4 h-4 mr-1" />Editar</Button>
            <Confirm title="¿Convertir en factura?" description="Se creará la factura y se descontará el inventario." onConfirm={() => post(`/quotes/${doc.id}/convert`, undefined, "Factura creada", true)} actionLabel="Convertir">
              <Button className="btn-neon" data-testid="view-convert-btn"><Repeat className="w-4 h-4 mr-1" />Convertir en factura</Button>
            </Confirm>
            <Confirm title="¿Anular proforma?" onConfirm={() => post(`/quotes/${doc.id}/void`, undefined, "Proforma anulada")} actionLabel="Anular">
              <Button variant="ghost" className="text-rose-400" data-testid="view-void-btn"><Ban className="w-4 h-4 mr-1" />Anular</Button>
            </Confirm>
          </>
        )}
        {!isQ && doc.status === "pendiente" && (
          <Button className="btn-neon" onClick={() => setPay(doc)} data-testid="view-pay-btn"><CheckCircle2 className="w-4 h-4 mr-1" />Registrar abono</Button>
        )}
        {!isQ && doc.status !== "anulada" && (
          <Confirm title="¿Anular factura?" description="El stock de los productos se devolverá al inventario." onConfirm={() => post(`/invoices/${doc.id}/status`, { status: "anulada" }, "Factura anulada")} actionLabel="Anular">
            <Button variant="ghost" className="text-rose-400" data-testid="view-void-btn"><Ban className="w-4 h-4 mr-1" />Anular</Button>
          </Confirm>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-slate-400">
        Estado: <StatusBadge status={doc.status} testId="view-status" />
        <EmailStatus doc={doc} />
        {doc.invoice_id && <Link to={`${base}/facturas/${doc.invoice_id}`} className="text-[#00F0FF] hover:underline mono" data-testid="view-invoice-link">Ver factura {doc.invoice_number}</Link>}
      </div>

      <div className="overflow-x-auto rounded-2xl">
        <div className="mx-auto shadow-[0_0_60px_rgba(0,240,255,0.12)] rounded-xl overflow-hidden" style={{ width: 816, minWidth: 816 }}>
          <div ref={ref} data-testid="document-paper"><PrintDocument doc={doc} kind={kind} s={settings} /></div>
        </div>
      </div>
      {!isQ && <PaymentsPanel doc={doc} onDelete={(p) => del(p)} />}
      <PrintPortal><PrintDocument doc={doc} kind={kind} s={settings} /></PrintPortal>
      <PayDialog invoice={pay} onOpenChange={(o) => !o && setPay(null)} onDone={load} />
    </div>
  );
}
